import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faArrowLeft, faUser } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import ModalCloseTicket from './ModalCloseTicket';

const getEstadoInfo = (estado, isDark = false) => {
  switch (estado) {
    case 1:
      return {
        text: 'Abierto',
        style: isDark
          ? { backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }
          : { backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }
      };
    case 2:
      return {
        text: 'Cerrado',
        style: isDark
          ? { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#f87171', border: '1px solid rgba(248, 113, 113, 0.3)' }
          : { backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }
      };
    case 3:
      return {
        text: 'En progreso',
        style: isDark
          ? { backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }
          : { backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }
      };
    default:
      return {
        text: `Estado #${estado || 1}`,
        style: isDark
          ? { backgroundColor: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }
          : { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }
      };
  }
};

const TicketChatHeader = ({ ticket, onBack, onTicketUpdated, onTicketClosed }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const estadoInfo = getEstadoInfo(ticket.iEstadoTicket, isDark);
  const isCerrado = ticket.iEstadoTicket === 2;

  const displayName = ticket.customerName 
    || (ticket.phone || ticket.ticketPhone ? `WhatsApp (${ticket.phone || ticket.ticketPhone})` : 'Cliente no asignado');

  return (
    <>
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0'
        }}
      >
        {/* Customer info & Back */}
        <div className="d-flex align-items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="btn btn-sm d-flex align-items-center justify-content-center shadow-none p-0"
              style={{
                width: '34px',
                height: '34px',
                minWidth: '34px',
                borderRadius: '8px',
                backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
                color: isDark ? '#94a3b8' : '#64748b'
              }}
              title="Volver a la lista de tickets"
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '0.85rem' }} />
            </button>
          )}

          <div
            className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
            style={{
              width: '42px',
              height: '42px',
              backgroundColor: isDark ? '#1e293b' : (ticket.initials ? '#dcfce7' : '#f1f5f9'),
              color: isDark ? '#34d399' : (ticket.initials ? '#15803d' : '#64748b'),
              fontSize: '0.95rem',
              border: isDark ? '1px solid #233142' : 'none'
            }}
          >
            {ticket.initials || <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.85rem' }} />}
          </div>

          <div>
            <div className="d-flex align-items-center gap-2">
              <h6 className="fw-bold mb-0" style={{ fontSize: '1rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                {displayName}
              </h6>
              <span
                className="badge border"
                style={{
                  fontSize: '0.7rem',
                  backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                  color: isDark ? '#94a3b8' : '#64748b',
                  borderColor: isDark ? '#334155' : '#e2e8f0'
                }}
              >
                Ticket #{ticket.id}
              </span>
              <span
                className="px-2 py-0.5 rounded fw-bold"
                style={{ fontSize: '0.70rem', ...estadoInfo.style }}
              >
                {estadoInfo.text}
              </span>
            </div>
            <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.76rem', color: '#16a34a' }}>
              <span
                className="rounded-circle"
                style={{ width: '6px', height: '6px', backgroundColor: '#16a34a' }}
              ></span>
              <span>En línea (WhatsApp)</span>
            </div>
          </div>
        </div>

        {/* Action buttons: Cerrar Ticket */}
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm px-3 py-1 rounded-2 d-flex align-items-center gap-2 shadow-none"
            style={{
              backgroundColor: isDark
                ? (isCerrado ? 'rgba(255,255,255,0.06)' : 'rgba(239, 68, 68, 0.2)')
                : (isCerrado ? '#f1f5f9' : '#fee2e2'),
              color: isDark
                ? (isCerrado ? '#64748b' : '#f87171')
                : (isCerrado ? '#94a3b8' : '#dc2626'),
              border: isDark
                ? (isCerrado ? '1px solid #334155' : '1px solid rgba(239, 68, 68, 0.4)')
                : 'none',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: isCerrado ? 'not-allowed' : 'pointer'
            }}
            onClick={() => setShowCloseModal(true)}
            disabled={isCerrado}
            title={isCerrado ? 'Este ticket ya está cerrado' : 'Cerrar ticket'}
          >
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{isCerrado ? 'Ticket Cerrado' : 'Cerrar Ticket'}</span>
          </button>
        </div>
      </div>

      {/* Modal separado para confirmar cierre de ticket */}
      <ModalCloseTicket
        show={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        ticket={ticket}
        onTicketClosed={onTicketClosed || onTicketUpdated}
      />
    </>
  );
};

TicketChatHeader.propTypes = {
  ticket: PropTypes.object.isRequired,
  onBack: PropTypes.func,
  onTicketUpdated: PropTypes.func,
  onTicketClosed: PropTypes.func
};

export default TicketChatHeader;
